import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import KpiCard from '../../components/KpiCard';
import ErrorBanner from '../../components/ErrorBanner';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { formatTRY, formatSayi } from '../../utils/analytics/format';
import { exportCsv } from '../../utils/analytics/export';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// SKT durum rozeti renkleri (skt_service enum'u ile aynı)
const SKT_ROZET = {
  GECTI:   { bg: '#fee2e2', fg: '#991b1b', label: 'SKT GEÇTİ' },
  KIRMIZI: { bg: '#fee2e2', fg: '#b91c1c', label: 'KIRMIZI' },
  SARI:    { bg: '#fef3c7', fg: '#92400e', label: 'SARI' },
  YESIL:   { bg: '#dcfce7', fg: '#166534', label: 'yeşil' },
  YOK:     { bg: '#f1f5f9', fg: '#64748b', label: '—' },
  BILINMIYOR: { bg: '#f1f5f9', fg: '#64748b', label: '?' },
};

const StokDevirRaporuPage = () => {
  useAuth();

  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const [lokasyonId, setLokasyonId] = useUrlState('lokasyon', '');
  const [sadeceRiskli, setSadeceRiskli] = useUrlState('riskli', '');

  // ── Okuma: tablodan (hesap YOK) ────────────────────────────────────────────
  const fetchRapor = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API_URL}/api/raporlar/stok-devir?`;
      const params = new URLSearchParams();
      if (lokasyonId) params.set('lokasyon_id', lokasyonId);
      if (sadeceRiskli) params.set('sadece_riskli', 'true');
      const res = await fetch(url + params.toString(), { headers: getAuthHeaders() });
      if (res.ok) {
        setData(await res.json());
      } else {
        const e = await res.json().catch(() => ({}));
        setError(e.detail || 'Rapor yüklenemedi');
      }
    } catch (err) {
      logError('Stok devir raporu yüklenemedi:', err);
      setError('Sunucu hatası — bağlantıyı kontrol edin');
    } finally {
      setLoading(false);
    }
  }, [lokasyonId, sadeceRiskli]);

  // ── Hesapla (rebuild): tabloyu tazeler ─────────────────────────────────────
  const rebuildRapor = async () => {
    setRebuildLoading(true);
    setError(null);
    setSuccess(null);
    try {
      let url = `${API_URL}/api/admin/rapor-stok-devir-rebuild`;
      if (lokasyonId) url += `?lokasyon_id=${lokasyonId}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Rapor hesaplanamadı');
      }
      const r = await res.json();
      setSuccess(`Rapor hesaplandı (${r.yazilan_satir} kalem · ${r.rapor_tarihi})`);
      await fetchRapor();
    } catch (err) {
      logError('Stok devir rebuild hatası:', err);
      setError('Rapor hesaplanırken hata oluştu');
    } finally {
      setRebuildLoading(false);
    }
  };

  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setLokasyonlar(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchRapor(); }, [fetchRapor]);

  const ozet = data?.ozet || {};
  const satirlar = data?.satirlar || [];

  const exportCSV = () => {
    if (!satirlar.length) return;
    exportCsv(satirlar.map(s => ({
      Malzeme: s.malzeme_ad || '',
      Kod: s.malzeme_kod || '',
      Lokasyon: s.lokasyon_ad || '',
      'Ürün Türü': s.urun_turu_ad || '',
      ABC: s.abc_sinifi || '',
      Miktar: s.mevcut_miktar,
      Birim: s.birim || '',
      'Stok Değeri (₺)': s.stok_degeri,
      'Devir Günü': s.devir_gun,
      'Yavaş Dönen': s.yavas_donen ? 'Evet' : '',
      'Hareketsiz Gün': s.hareketsiz_gun,
      'Ölü Stok': s.olu_stok ? 'Evet' : '',
      'SKT Durum': s.skt_durum,
      'SKT Kala (gün)': s.skt_kala_gun,
      'Riskli Sermaye': s.riskli_sermaye ? 'Evet' : '',
      Öneri: s.oneri || '',
    })), `stok-devir-raporu-${data?.rapor_tarihi || ''}.csv`);
  };

  const SktRozet = ({ durum }) => {
    const r = SKT_ROZET[durum] || SKT_ROZET.YOK;
    return (
      <span style={{
        padding: '2px 8px', borderRadius: radius.sm, fontSize: '11px', fontWeight: '600',
        background: r.bg, color: r.fg,
      }}>{r.label}</span>
    );
  };

  return (
    <div>
      <Header title="Stok Devir · Bozulma · Değerleme (Rapor 5)" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Filtre + Hesapla Bar */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '16px 20px', boxShadow: shadows.md, marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={lokasyonId} onChange={e => setLokasyonId(e.target.value)}
              style={{ padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px', background: colors.surface }}>
              <option value="">Tüm Lokasyonlar</option>
              {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
            </select>

            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#555', cursor: 'pointer' }}>
              <input type="checkbox" checked={!!sadeceRiskli}
                onChange={e => setSadeceRiskli(e.target.checked ? '1' : '')} />
              Sadece riskli
            </label>

            <div style={{ width: '1px', height: '24px', background: '#e0e0e0', margin: '0 4px' }} />

            <button onClick={rebuildRapor} disabled={rebuildLoading} style={{
              padding: '7px 16px', border: 'none', borderRadius: radius.sm,
              background: '#2563eb', color: '#fff', cursor: rebuildLoading ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: '600', opacity: rebuildLoading ? 0.6 : 1,
            }}>{rebuildLoading ? 'Hesaplanıyor...' : 'Rapor Hesapla'}</button>

            <button onClick={exportCSV} disabled={!satirlar.length} style={{
              padding: '7px 16px', border: `1px solid ${styles.primary}`, borderRadius: radius.sm,
              background: colors.surface, color: styles.primary, cursor: satirlar.length ? 'pointer' : 'not-allowed',
              fontSize: '13px', fontWeight: '600', opacity: satirlar.length ? 1 : 0.5,
            }}>CSV İndir</button>

            <span style={{ marginLeft: 'auto', fontSize: '12px', color: colors.textTertiary }}>
              {data?.rapor_tarihi ? `Snapshot: ${data.rapor_tarihi}` : 'Henüz hesaplanmadı'}
            </span>
          </div>
        </div>

        <div style={{ fontSize: '12px', color: colors.textTertiary, marginBottom: '20px', lineHeight: '1.5' }}>
          Değerler son "Rapor Hesapla" anındaki snapshot'tan okunur. Eşikler ürün türüne göre parametriktir (Ürün Türleri ekranı).
          SKT/bozulma detayı için Bölüm 4 (SKT-Bozulma Raporu). Riskli sermaye = yavaş dönen + SKT yaklaşan + ölü stok.
        </div>

        <ErrorBanner error={error} onRetry={fetchRapor} />
        {success && (
          <div style={{ padding: '10px 14px', background: '#f0fdf4', color: '#166534', borderRadius: radius.md, marginBottom: '16px', fontSize: '13px', fontWeight: '500' }}>
            {success}
          </div>
        )}

        {loading && !data ? (
          <div style={{ textAlign: 'center', padding: '60px', color: colors.textTertiary }}>Yükleniyor...</div>
        ) : !data || !satirlar.length ? (
          !error && (
            <div style={{ textAlign: 'center', padding: '60px', color: colors.textTertiary }}>
              Snapshot bulunamadı. "Rapor Hesapla" ile ilk hesabı oluşturun.
            </div>
          )
        ) : (
          <>
            {/* KPI — Bölüm 9: Bağlı sermaye özeti */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <KpiCard title="Toplam Bağlı Sermaye" value={formatTRY(ozet.toplam_bagli_sermaye || 0)} color={styles.info} icon="🔒" />
              <KpiCard title={`Riskli Sermaye (%${formatSayi(ozet.riskli_oran || 0, { decimals: 0 })})`} value={formatTRY(ozet.riskli_sermaye_tl || 0)} color={styles.danger} icon="⚠️" />
              <KpiCard title="Yavaş Dönen" value={formatSayi(ozet.yavas_donen_sayisi || 0, { decimals: 0 })} color={styles.warning} icon="🐌" />
              <KpiCard title="Ölü Stok" value={formatTRY(ozet.olu_stok_tl || 0)} color="#7c3aed" icon="💀" />
            </div>

            {/* Bölüm 8: Değer kırılımları */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <KirilimTablo baslik="Ürün Türü Bazında Değer" rows={ozet.kirilim_urun_turu} />
              <KirilimTablo baslik="ABC Sınıfı Bazında Değer" rows={ozet.kirilim_abc} />
            </div>

            {/* Kalem detay */}
            <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>
                Kalem Detayı ({satirlar.length}) — riskli önce
              </h3>
              <div style={{ overflowX: 'auto', maxHeight: '640px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
                      {['Malzeme', 'Tür', 'ABC', 'Miktar', 'Değer ₺', 'Devir (gün)', 'Hareketsiz', 'SKT', 'Öneri'].map(h => (
                        <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#555', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: colors.backgroundAlt }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {satirlar.map((s, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0', background: s.riskli_sermaye ? '#fffbeb' : 'transparent' }}>
                        <td style={{ padding: '8px', fontWeight: '500' }}>
                          {s.malzeme_ad}
                          <span style={{ color: '#aaa', fontSize: '11px', marginLeft: 6 }}>{s.malzeme_kod}</span>
                        </td>
                        <td style={{ padding: '8px', color: '#666' }}>{s.urun_turu_ad || '—'}</td>
                        <td style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#555' }}>{s.abc_sinifi || '—'}</td>
                        <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                          {formatSayi(s.mevcut_miktar)} <span style={{ color: '#999', fontSize: '11px' }}>{s.birim}</span>
                        </td>
                        <td style={{ padding: '8px', fontWeight: '600' }}>{formatTRY(s.stok_degeri, { decimals: 0 })}</td>
                        <td style={{ padding: '8px', color: s.yavas_donen ? styles.danger : '#333', fontWeight: s.yavas_donen ? '600' : '400' }}>
                          {s.devir_gun != null ? formatSayi(s.devir_gun, { decimals: 0 }) : '—'}
                        </td>
                        <td style={{ padding: '8px', color: s.olu_stok ? '#7c3aed' : '#888', fontWeight: s.olu_stok ? '600' : '400' }}>
                          {s.hareketsiz_gun != null ? `${s.hareketsiz_gun} gün` : '—'}
                        </td>
                        <td style={{ padding: '8px' }}><SktRozet durum={s.skt_durum} /></td>
                        <td style={{ padding: '8px', color: '#555', fontSize: '12px', maxWidth: 260 }}>{s.oneri || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Küçük yardımcı: kırılım tablosu (ürün türü / ABC)
const KirilimTablo = ({ baslik, rows }) => (
  <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
    <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', color: '#333' }}>{baslik}</h3>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
      <thead>
        <tr style={{ borderBottom: `2px solid ${colors.border}`, color: '#555' }}>
          <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600' }}>Kırılım</th>
          <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>Değer ₺</th>
          <th style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>%</th>
        </tr>
      </thead>
      <tbody>
        {(rows || []).map((r, i) => (
          <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
            <td style={{ padding: '8px' }}>{r.ad}</td>
            <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600' }}>{formatTRY(r.deger, { decimals: 0 })}</td>
            <td style={{ padding: '8px', textAlign: 'right', color: '#888' }}>%{formatSayi(r.oran, { decimals: 1 })}</td>
          </tr>
        ))}
        {(!rows || !rows.length) && (
          <tr><td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: colors.textTertiary }}>Veri yok</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default StokDevirRaporuPage;
